import { Crop, centerCrop, makeAspectCrop } from 'react-image-crop';
import { compressImage, convertBase64 } from 'utils/helpers';

interface CenterAspectCropParams {
  imageWidth: number;
  imageHeight: number;
  ratio: number;
  cropWidth?: number;
  cropHeight?: number;
}

export const centerAspectCrop = (params: CenterAspectCropParams) => {
  const { imageWidth, imageHeight, cropWidth, cropHeight, ratio } = params;

  if (cropWidth || cropHeight) {
    return centerCrop(
      makeAspectCrop(
        {
          unit: 'px',
          width: cropWidth,
          height: cropHeight,
        },
        ratio,
        imageWidth,
        imageHeight,
      ),
      imageWidth,
      imageHeight,
    );
  }

  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 50,
        height: 50,
      },
      ratio,
      imageWidth,
      imageHeight,
    ),
    imageWidth,
    imageHeight,
  );
};

const extractBase64ImageFromCanvas = async (
  canvas: HTMLCanvasElement,
  fileType: 'image/jpeg' | 'image/png',
): Promise<string> => {
  const imageFile = await new Promise<File | null>(resolve => {
    canvas.toBlob(blob => {
      if (!blob) return resolve(null);

      const file = new File([blob], 'image', {
        type: fileType,
      });

      resolve(file);
    }, fileType);
  });

  if (!imageFile) return '';

  const compressed = await compressImage({ file: imageFile });

  const base64 = await convertBase64(compressed);

  return String(base64);
};

const maxWidthOrHeight = 8610;

interface GetBase64FromCropParams {
  image: HTMLImageElement;
  fileType: 'image/jpeg' | 'image/png';
  crop?: Crop;
  scale?: number;
}

export const getBase64FromCrop = async (params: GetBase64FromCropParams) => {
  const { image, crop, scale = 1, fileType } = params;

  if (!crop) throw new Error('Crop is required');

  const canvas = document.createElement('canvas');

  const ctx = canvas.getContext('2d');

  if (!ctx) throw new Error('Canvas context is null');

  const shouldResize =
    image.naturalWidth > maxWidthOrHeight ||
    image.naturalHeight > maxWidthOrHeight;

  const getDimensions = () => {
    if (!shouldResize) {
      return { width: image.naturalWidth, height: image.naturalHeight };
    }

    const aspectRatio = image.naturalWidth / image.naturalHeight;
    let newWidth = image.naturalWidth;
    let newHeight = image.naturalHeight;

    if (image.naturalWidth > image.naturalHeight) {
      newWidth = maxWidthOrHeight;
      newHeight = Math.round(maxWidthOrHeight / aspectRatio);
    } else {
      newHeight = maxWidthOrHeight;
      newWidth = Math.round(maxWidthOrHeight * aspectRatio);
    }

    return { width: newWidth, height: newHeight };
  };

  const { width, height } = getDimensions();

  const scaleX = width / image.width;
  const scaleY = height / image.height;
  const pixelRatio = window.devicePixelRatio;

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const centerX = width / 2;
  const centerY = height / 2;

  ctx.save();

  // 4) Move the crop origin to the canvas origin (0,0)
  ctx.translate(-cropX, -cropY);
  // 3) Move the origin to the center of the original position
  ctx.translate(centerX, centerY);
  // 2) Scale the image
  ctx.scale(scale, scale);
  // 1) Move the center of the image to the origin (0,0)
  ctx.translate(-centerX, -centerY);

  if (shouldResize) {
    ctx.drawImage(image, 0, 0, width, height);

    ctx.restore();

    const result = await extractBase64ImageFromCanvas(canvas, fileType);

    canvas.remove();

    return result;
  }

  ctx.drawImage(image, 0, 0, width, height, 0, 0, width, height);

  ctx.restore();

  const result = await extractBase64ImageFromCanvas(canvas, fileType);

  canvas.remove();

  return result;
};
